import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/shared/api/client";
import { invalidateTenantScopes } from "@/shared/api/invalidate";
import { queryKeys } from "@/shared/api/query-keys";
import type { CancelDeleteResult, UsageDto } from "@/shared/api/types";
import { useTenantId } from "@/shared/auth/session";
import { USAGE_STALE_MS } from "@/shared/constants/limits";

/**
 * Plan, limits vs usage, retention, feature flags, trial and lifecycle state (ADR-0006 §4): the ONE source for
 * every plan gate in the portal. Reachable by every member; the API enforces the same gates on every write.
 */
export function useEntitlementsQuery() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.tenant.settings.usage(tenantId),
    queryFn: ({ signal }) => apiGet<UsageDto>("/tenants/current/usage", undefined, signal),
    staleTime: USAGE_STALE_MS,
  });
}

/** The shell banner and the danger zone both offer it: a cancelled deletion changes /me (status) and the usage DTO. */
export function useCancelDelete() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: () => apiPost<CancelDeleteResult>("/tenants/current/cancel-delete"),
    onSuccess: async () => {
      await Promise.all([
        invalidateTenantScopes(queryClient, tenantId, ["settings", "organisation"]),
        queryClient.invalidateQueries({ queryKey: queryKeys.session.me() }),
      ]);
    },
  });
}
